import { useState, useEffect, useContext } from "react";
import ReactGA from "react-ga4";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Link,
} from "@mui/material";
import DataTable from "./components/DataTable";
import SalesVelocity from "./components/SalesVelocity";
import CustomReporting from "./components/CustomReporting";
import { EventContext } from "../../contexts/EventContext";
import requests from "../../utilities/requests";

const Reports = () => {
  const { userData } = useContext(EventContext);
  const [selectedReport, setSelectedReport] = useState(null);
  const [data, setData] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({});
  const [tablePageSize, setTablePageSize] = useState(20);
  const [tablePageIndex, setTablePageIndex] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);
  const [downloadLink, setDownloadLink] = useState(null);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/reports",
      title: "Reports",
    });
  }, []);

  useEffect(() => {
    if (selectedReport) {
      if (selectedReport === "custom") {
        console.log("custom");
      } else {
        getData();
      }
    }
  }, [selectedReport, query]);

  const getData = async () => {
    setLoading(true);
    const result = await requests(
      `reports?reportName=${selectedReport}${query}`
    );
    if (result.success) {
      if (result.fields) {
        setFields(result.fields);
      } else {
        setFields([]);
      }
      setData(result.data);
    }
    setLoading(false);
  };

  const loadMore = async () => {
    setLoading(true);
    const currentSkip = data.length; // use current data length as skip value
    const result = await requests(
      `reports?reportName=${selectedReport}${query}&skip=${currentSkip}&limit=100`
    );
    if (result.success) {
      setData((prev) => [...prev, ...result.data]);
    }
    setLoading(false);
  };

  const handleUpdate = (filters) => {
    setAppliedFilters(filters);
    if (Object.keys(filters).length === 0) setQuery("");
    let q = "";
    for (let field of Object.keys(filters)) {
      if (field === "age") {
        if (filters.age.gte) {
          q = q + `&age_gte=${filters.age.gte}`;
        }
        if (filters.age.lte) {
          q = q + `&age_lte=${filters.age.lte}`;
        }
      } else {
        q = q + `&${field}=${filters[field]}`;
      }
    }
    setQuery(q);
  };

  const renderReport = () => {
    if (loading) {
      return (
        <div
          style={{
            width: "100%",
            height: "500px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={75} />
        </div>
      );
    }
    if (selectedReport === 'salesVelocity') {
      return <SalesVelocity data={data} />
    }
    if (selectedReport && data.length > 0) {
      return (
        <DataTable
          data={data}
          fields={fields}
          update={handleUpdate}
          initialFilterValues={appliedFilters}
          loadMore={loadMore}
          initialPageSize={tablePageSize}
          initialPageIndex={tablePageIndex}
          onPageSizeChange={setTablePageSize}
          onPageIndexChange={setTablePageIndex}
          handleExport={handleExport}
        />
      );
    } else if (selectedReport && data.length === 0) {
      return (
        <div
          style={{
            width: "100%",
            height: "500px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography align="center">No data to display</Typography>
        </div>
      );
    }

    return null;
  };

  const handleExport = async (action) => {
    setExportLoading(true);
    let url = `reports/export?reportName=${selectedReport}`;
    if (action === "filtered") {
      url = url + query;
    }
    const result = await requests(url, "post", {});
    if (result.success) {
      setDownloadLink(result.url);
    }
    setExportLoading(false);
  };

  return (
    <Grid container justifyContent="center" spacing={3}>
      <Grid item xs={6}>
        <FormControl fullWidth>
          <InputLabel>Select a Report</InputLabel>
          <Select
            value={selectedReport || ""}
            label="Select a Report"
            onChange={(e) => setSelectedReport(e.target.value)}
          >
            <MenuItem value={"users"}>Users</MenuItem>
            <MenuItem value={"stats"}>Event Stats</MenuItem>
            <MenuItem value={"salesVelocity"}>Sales Velocity</MenuItem>
            <MenuItem value={"noResponse"}>No Reponse</MenuItem>
            {userData?.org?.enhancedReporting ? (
              <MenuItem value={"custom"}>Custom</MenuItem>
            ) : null}
          </Select>
        </FormControl>
      </Grid>
      {exportLoading ? (
        <Grid
          item
          xs={12}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <CircularProgress size={40} />
        </Grid>
      ) : null}
      {downloadLink ? (
        <Grid
          item
          xs={12}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <Link href={downloadLink}>Download Report</Link>
        </Grid>
      ) : null}

      <Grid item xs={12}>
        {userData?.org?.enhancedReporting && selectedReport === "custom" ? (
          <CustomReporting />
        ) : (
          renderReport()
        )}
      </Grid>
    </Grid>
  );
};

export default Reports;
